import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  InboxStackIcon,
  ShieldCheckIcon,
  TruckIcon,
  UserGroupIcon,
  UserPlusIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { collection, getDocs, onSnapshot, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { buildFunctionUrl } from '../../config/functions';

const operationViews = [
  { id: 'tasks', label: 'Tareas y flujo' },
  { id: 'team', label: 'Equipo y accesos' },
];

const typeTabs = [
  { value: 'all', label: 'Todo', icon: InboxStackIcon },
  { value: 'warehouse', label: 'Almacén', icon: TruckIcon },
  { value: 'technician', label: 'Instalación', icon: WrenchScrewdriverIcon },
  { value: 'delivery', label: 'Entrega', icon: ClipboardDocumentCheckIcon },
  { value: 'service', label: 'Servicio', icon: UserGroupIcon },
];

const statusOptions = [
  { value: 'pending', label: 'Pendiente' },
  { value: 'in_progress', label: 'En proceso' },
  { value: 'blocked', label: 'En espera' },
  { value: 'completed', label: 'Completada' },
];

const statusBadges = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  in_progress: 'bg-sky-100 text-sky-800 border-sky-200',
  blocked: 'bg-rose-100 text-rose-800 border-rose-200',
  completed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
};

const roleOptions = [
  { value: 'seller', label: 'Ventas / Leads' },
  { value: 'warehouse', label: 'Almacén' },
  { value: 'technician', label: 'Técnicos' },
  { value: 'service', label: 'Servicio' },
  { value: 'admin', label: 'Admin' },
];

const roleLabels = {
  seller: 'Ventas',
  warehouse: 'Almacén',
  technician: 'Instalaciones',
  service: 'Servicio',
  admin: 'Administración',
  delivery: 'Entrega',
};

const defaultDepartmentByRole = {
  seller: 'Ventas',
  warehouse: 'Almacén',
  technician: 'Instalaciones',
  service: 'Servicio',
  admin: 'Administración',
};

const rolePermissionMap = {
  seller: ['Ver/crear leads', 'Generar estimados', 'Emitir facturas', 'Registrar pagos parciales'],
  warehouse: ['Ver tareas de almacén', 'Confirmar preparación', 'Escalar incidencias'],
  technician: ['Calendario de instalaciones', 'Registrar entregas', 'Confirmar cobros finales'],
  service: ['Casos de soporte', 'Chat con cliente', 'Actualizar estado postventa'],
  admin: ['Acceso completo'],
};

const initialFormState = {
  type: 'warehouse',
  title: '',
  description: '',
  relatedOrderId: '',
  relatedInvoiceId: '',
  relatedLeadId: '',
  clientName: '',
  clientEmail: '',
  clientPhone: '',
  projectLocation: '',
  lama: '',
  glass: '',
  color: '',
  dueDate: '',
  accessoriesText: '',
  itemsText: '',
  metadataNotes: '',
  deposit: '',
  total: '',
  assigneeName: '',
  assigneeEmail: '',
  invoiceAttachmentUrl: '',
};

const initialMemberForm = {
  name: '',
  email: '',
  phone: '',
  role: 'seller',
  department: defaultDepartmentByRole.seller,
  notes: '',
};

const parseListInput = (raw = '') =>
  raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [name, qty, notes] = line.split('|').map((segment) => segment?.trim());
      const quantity = qty ? Number(qty) : undefined;
      return {
        name,
        quantity: Number.isFinite(quantity) ? quantity : undefined,
        notes: notes || undefined,
      };
    })
    .filter((entry) => entry.name);

const firestoreDateToJs = (value) => {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  if (value.seconds) return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (value) => {
  const date = firestoreDateToJs(value);
  if (!date) return 'Sin fecha';
  return date.toLocaleDateString('es-PR', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatRelative = (value) => {
  const date = firestoreDateToJs(value);
  if (!date) return 'Fecha pendiente';
  const now = Date.now();
  const diff = date.getTime() - now;
  const days = Math.round(diff / (1000 * 60 * 60 * 24));
  if (Math.abs(days) >= 1) {
    const formatter = new Intl.RelativeTimeFormat('es', { numeric: 'auto' });
    return formatter.format(days, 'day');
  }
  const hours = Math.round(diff / (1000 * 60 * 60));
  const formatter = new Intl.RelativeTimeFormat('es', { numeric: 'auto' });
  return formatter.format(hours || 0, 'hour');
};

const OperationsBoard = () => {
  const [viewMode, setViewMode] = useState('tasks');
  const [tasks, setTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [activeType, setActiveType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [teamLoading, setTeamLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [form, setForm] = useState(initialFormState);
  const [teamForm, setTeamForm] = useState(initialMemberForm);
  const [feedback, setFeedback] = useState(null);
  const [teamFeedback, setTeamFeedback] = useState(null);
  const [updatingTask, setUpdatingTask] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [clientInvoices, setClientInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('');

  useEffect(() => {
    const tasksQuery = query(collection(db, 'operations_tasks'), orderBy('createdAt', 'desc'));
    const unsubscribeTasks = onSnapshot(
      tasksQuery,
      (snapshot) => {
        const nextTasks = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setTasks(nextTasks);
        setLoading(false);
      },
      (error) => {
        console.error('❌ Error leyendo tareas:', error);
        setLoading(false);
      }
    );

    const membersQuery = query(collection(db, 'teamMembers'), orderBy('updatedAt', 'desc'));
    const unsubscribeMembers = onSnapshot(
      membersQuery,
      (snapshot) => {
        const data = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setTeamMembers(data);
        setTeamLoading(false);
      },
      (error) => {
        console.error('❌ Error leyendo equipo:', error);
        setTeamLoading(false);
      }
    );

    return () => {
      unsubscribeTasks();
      unsubscribeMembers();
    };
  }, []);

  useEffect(() => {
    const fetchClients = async () => {
      setClientsLoading(true);
      try {
        const snap = await getDocs(collection(db, 'clients'));
        const data = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        data.sort((a, b) => (a.businessName || a.name || '').localeCompare(b.businessName || b.name || ''));
        setClients(data);
      } catch (error) {
        console.error('❌ Error cargando clientes:', error);
      } finally {
        setClientsLoading(false);
      }
    };
    fetchClients();
  }, []);

  const filteredTasks = useMemo(() => {
    if (activeType === 'all') return tasks;
    return tasks.filter((task) => task.type === activeType);
  }, [tasks, activeType]);

  const stats = useMemo(() => {
    const total = tasks.length;
    const pending = tasks.filter((task) => task.status === 'pending').length;
    const blocked = tasks.filter((task) => task.status === 'blocked').length;
    const dueSoon = tasks.filter((task) => {
      const date = firestoreDateToJs(task.dueDate);
      if (!date) return false;
      const diff = date.getTime() - Date.now();
      return diff <= 1000 * 60 * 60 * 24 * 3 && task.status !== 'completed';
    }).length;

    return { total, pending, blocked, dueSoon };
  }, [tasks]);

  const teamStats = useMemo(() => {
    const total = teamMembers.length;
    const byRole = roleOptions.reduce((acc, option) => {
      acc[option.value] = teamMembers.filter((member) => member.role === option.value).length;
      return acc;
    }, {});
    return { total, byRole };
  }, [teamMembers]);

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleTeamFormChange = (event) => {
    const { name, value } = event.target;
    setTeamForm((prev) => {
      if (name === 'role') {
        return {
          ...prev,
          role: value,
          department: defaultDepartmentByRole[value] || prev.department || 'Operaciones',
        };
      }
      return { ...prev, [name]: value };
    });
  };
  const hydrateFormWithClient = (client) => {
    if (!client) return;
    setForm((prev) => ({
      ...prev,
      clientName: client.businessName || client.name || prev.clientName,
      clientEmail: client.email || prev.clientEmail,
      clientPhone: client.phone || client.contactPhone || prev.clientPhone,
      projectLocation: client.projectLocation || client.address || prev.projectLocation,
    }));
  };

  const hydrateFormWithInvoice = (invoice) => {
    if (!invoice) return;

    const summary = invoice.catalogSummary || null;
    const summaryItems = summary?.models?.length
      ? summary.models
      : null;

    const itemsText = summaryItems
      ? summaryItems
          .map((model) => {
            const base = `${model.name || 'Modelo'} | ${model.quantity || 1} | ${model.width || '--'}" x ${
              model.height || '--'
            }"`;
            const extras = [model.series ? `Serie ${model.series.replace('lama', 'Lama ')}` : null]
              .filter(Boolean)
              .join(' · ');
            return extras ? `${base} · ${extras}` : base;
          })
          .join('\n')
      : (invoice.items || [])
          .map((item) => `${item.name || 'Modelo'} | ${item.quantity || 1} | ${item.description || ''}`.trim())
          .join('\n');

    const accessoriesFromSummary = summary?.accessories?.length
      ? summary.accessories
      : null;
    const accessoriesText = accessoriesFromSummary
      ? accessoriesFromSummary
          .map(
            (addon) =>
              `${addon.name || 'Accesorio'} | ${addon.quantity || 1} | ${addon.description || ''}`.trim()
          )
          .join('\n')
      : (invoice.accessories || [])
          .map((addon) =>
            typeof addon === 'string'
              ? addon
              : `${addon.name || 'Accesorio'}${addon.quantity ? ` | ${addon.quantity}` : ''}${
                  addon.notes || addon.description ? ` | ${addon.notes || addon.description}` : ''
                }`
          )
          .join('\n');

    const primaryModel = summaryItems?.[0] || null;

    setForm((prev) => ({
      ...prev,
      relatedOrderId: invoice.orderNumber || invoice.reference || invoice.id || prev.relatedOrderId,
      relatedInvoiceId: invoice.id || prev.relatedInvoiceId,
      deposit: invoice.deposit ? String(invoice.deposit) : prev.deposit,
      total:
        typeof invoice.total !== 'undefined'
          ? String(invoice.total)
          : prev.total,
      itemsText: itemsText || prev.itemsText,
      accessoriesText: accessoriesText || prev.accessoriesText,
      invoiceAttachmentUrl: invoice.invoicePdfUrl || prev.invoiceAttachmentUrl,
      lama: primaryModel?.series || invoice.lama || invoice.metadata?.lama || prev.lama,
      glass: primaryModel?.glass || invoice.glass || invoice.metadata?.glass || prev.glass,
      color: primaryModel?.color || invoice.color || invoice.metadata?.color || prev.color,
    }));
  };

  const handleClientSelection = async (value) => {
    setSelectedClientId(value);
    setSelectedInvoiceId('');
    setClientInvoices([]);
    if (!value) {
      setForm((prev) => ({
        ...prev,
        clientName: '',
        clientEmail: '',
        clientPhone: '',
        projectLocation: '',
      }));
      return;
    }
    const client = clients.find((item) => item.id === value);
    hydrateFormWithClient(client);
    setInvoicesLoading(true);
    try {
      const snap = await getDocs(collection(db, 'clients', value, 'invoices'));
      const invoices = snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      invoices.sort(
        (a, b) =>
          (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)
      );
      setClientInvoices(invoices);
    } catch (error) {
      console.error('❌ Error cargando facturas del cliente:', error);
      setClientInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const handleInvoiceSelection = (value) => {
    setSelectedInvoiceId(value);
    if (!value) return;
    const invoice = clientInvoices.find((item) => item.id === value);
    hydrateFormWithInvoice(invoice);
  };

  const handleCreateTask = async (event) => {
    event.preventDefault();
    setFeedback(null);
    if (!form.invoiceAttachmentUrl?.trim()) {
      setFeedback({
        type: 'error',
        message: 'Adjunta el PDF o enlace de la factura antes de enviar instrucciones.',
      });
      return;
    }
    setCreating(true);
    try {
      const callable = httpsCallable(functions, 'createOperationalTask');
      const payload = {
        type: form.type,
        title: form.title,
        description: form.description,
        relatedOrderId: form.relatedOrderId,
        relatedInvoiceId: form.relatedInvoiceId || form.relatedOrderId,
        relatedLeadId: form.relatedLeadId,
        clientId: selectedClientId || null,
        client: {
          name: form.clientName,
          email: form.clientEmail,
          phone: form.clientPhone,
          projectLocation: form.projectLocation,
        },
        metadata: {
          lama: form.lama,
          glass: form.glass,
          color: form.color,
          notes: form.metadataNotes,
          deposit: form.deposit ? Number(form.deposit) : undefined,
          total: form.total ? Number(form.total) : undefined,
          items: parseListInput(form.itemsText),
          accessories: parseListInput(form.accessoriesText),
          invoiceUrl: form.invoiceAttachmentUrl?.trim(),
        },
        assignees:
          form.assigneeEmail || form.assigneeName
            ? [
                {
                  name: form.assigneeName,
                  email: form.assigneeEmail,
                  role: form.type,
                },
              ]
            : undefined,
        dueDate: form.dueDate || null,
        attachments: [
          {
            type: 'invoice',
            invoiceId: form.relatedInvoiceId || form.relatedOrderId || 'sin-id',
            url: form.invoiceAttachmentUrl.trim(),
            label: `Factura ${form.relatedInvoiceId || form.relatedOrderId || ''}`,
          },
        ],
      };

      await callable(payload);
      setForm(initialFormState);
      setFeedback({ type: 'success', message: 'Tarea creada y enviada a los equipos correspondidos.' });
    } catch (error) {
      console.error('❌ Error creando tarea:', error);
      setFeedback({
        type: 'error',
        message: error.message || 'No se pudo crear la tarea. Inténtalo otra vez.',
      });
    } finally {
      setCreating(false);
    }
  };

  const handleInviteMember = async (event) => {
    event.preventDefault();
    if (!teamForm.email || !teamForm.name) {
      setTeamFeedback({ type: 'error', message: 'Nombre y correo son obligatorios.' });
      return;
    }
    setInviting(true);
    setTeamFeedback(null);
    try {
      const response = await fetch(buildFunctionUrl('/inviteTeamMember'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: teamForm.name.trim(),
          email: teamForm.email.trim().toLowerCase(),
          phone: teamForm.phone?.trim() || '',
          role: teamForm.role,
          department: teamForm.department?.trim(),
          notes: teamForm.notes,
          permissions: rolePermissionMap[teamForm.role],
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'No se pudo invitar al colaborador.');
      }
      setTeamForm(initialMemberForm);
      setTeamFeedback({ type: 'success', message: 'Invitación enviada. El colaborador recibirá su enlace.' });
    } catch (error) {
      console.error('❌ Error invitando colaborador:', error);
      setTeamFeedback({
        type: 'error',
        message: error.message || 'No se pudo enviar la invitación.',
      });
    } finally {
      setInviting(false);
    }
  };

  const handleStatusUpdate = async (taskId, status) => {
    setUpdatingTask(taskId);
    try {
      const callable = httpsCallable(functions, 'updateOperationalTaskStatus');
      await callable({ taskId, status });
      setFeedback({ type: 'success', message: 'Estado actualizado correctamente.' });
    } catch (error) {
      console.error('❌ Error actualizando tarea:', error);
      setFeedback({
        type: 'error',
        message: error.message || 'No se pudo actualizar la tarea.',
      });
    } finally {
      setUpdatingTask('');
    }
  };

  const highlightClass = (type) => {
    switch (type) {
      case 'warehouse':
        return 'border-emerald-200';
      case 'technician':
        return 'border-sky-200';
      case 'delivery':
        return 'border-amber-200';
      case 'service':
        return 'border-slate-200';
      default:
        return 'border-[var(--admin-border)]';
    }
  };

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-8 px-4 py-8">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">Operaciones</p>
            <h1 className="text-2xl font-bold text-[var(--admin-text)]">Controla entregas, técnicos y equipo</h1>
            <p className="max-w-3xl text-sm text-[var(--admin-muted)]">
              Admin centraliza cada orden. Aquí defines responsables, programaciones, cobros pendientes y acceso del personal.
            </p>
          </div>
          <div className="w-full sm:w-auto overflow-x-auto">
            <div className="flex flex-nowrap gap-2 min-w-max">
              {operationViews.map((tab) => {
                const active = viewMode === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setViewMode(tab.id);
                      setFeedback(null);
                      setTeamFeedback(null);
                    }}
                    className={[
                      'flex-none whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition',
                      active
                        ? 'bg-[var(--admin-primary)] text-white shadow'
                        : 'border border-[var(--admin-border)] text-[var(--admin-muted)] hover:border-[var(--admin-primary)] hover:text-[var(--admin-primary)]',
                    ].join(' ')}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        {(feedback || teamFeedback) && (
          <div
            className={[
              'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold',
              (feedback?.type || teamFeedback?.type) === 'success'
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-rose-100 text-rose-700',
            ].join(' ')}
          >
            {(feedback?.type || teamFeedback?.type) === 'success' ? (
              <CheckCircleIcon className="h-4 w-4" />
            ) : (
              <ClockIcon className="h-4 w-4" />
            )}
            {feedback?.message || teamFeedback?.message}
          </div>
        )}
      </div>

      {viewMode === 'tasks' && (
        <>
          <div className="grid gap-3 sm:grid-cols-4">
            <div className="rounded-2xl border border-[var(--admin-border)] bg-[var(--admin-surface)] p-4">
              <p className="text-xs uppercase tracking-[0.35em] text-[var(--admin-muted)]">En cola</p>
              <p className="text-2xl font-semibold text-[var(--admin-text)]">{stats.total}</p>
            </div>
            <div className="rounded-2xl border border-[var(--admin-border)] bg-[var(--admin-surface)] p-4">
              <p className="text-xs uppercase tracking-[0.35em] text-[var(--admin-muted)]">Pendientes</p>
              <p className="text-2xl font-semibold text-amber-600">{stats.pending}</p>
            </div>
            <div className="rounded-2xl border border-[var(--admin-border)] bg-[var(--admin-surface)] p-4">
              <p className="text-xs uppercase tracking-[0.35em] text-[var(--admin-muted)]">Bloqueadas</p>
              <p className="text-2xl font-semibold text-rose-600">{stats.blocked}</p>
            </div>
            <div className="rounded-2xl border border-[var(--admin-border)] bg-[var(--admin-surface)] p-4">
              <p className="text-xs uppercase tracking-[0.35em] text-[var(--admin-muted)]">Vencen en 3 días</p>
              <p className="text-2xl font-semibold text-sky-600">{stats.dueSoon}</p>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
            <section className="rounded-3xl border border-[var(--admin-border)] bg-white p-5 shadow-sm">
              <div className="overflow-x-auto">
                <div className="flex flex-nowrap items-center gap-3 min-w-max">
                  {typeTabs.map(({ value, label, icon: Icon }) => {
                    const active = value === activeType;
                    return (
                      <button
                        key={value}
                        onClick={() => setActiveType(value)}
                        className={[
                          'inline-flex flex-none items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-semibold transition',
                          active
                            ? 'border-transparent bg-[var(--admin-primary)] text-white shadow'
                            : 'border-[var(--admin-border)] text-[var(--admin-muted)] hover:border-[var(--admin-primary)] hover:text-[var(--admin-primary)]',
                        ].join(' ')}
                      >
                        <Icon className="h-4 w-4" />
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-5 space-y-4">
                {loading && (
                  <div className="rounded-2xl border border-dashed border-[var(--admin-border)] p-6 text-center text-sm text-[var(--admin-muted)]">
                    Cargando tareas...
                  </div>
                )}
                {!loading && filteredTasks.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-[var(--admin-border)] p-6 text-center text-sm text-[var(--admin-muted)]">
                    No hay tareas registradas para este filtro.
                  </div>
                )}

                {filteredTasks.map((task) => {
                  const StatusIcon = task.status === 'completed' ? CheckCircleIcon : ArrowPathIcon;
                  return (
                    <article
                      key={task.id}
                      className={[
                        'rounded-3xl border bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md',
                        highlightClass(task.type),
                      ].join(' ')}
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex-1">
                          <p className="text-xs uppercase tracking-[0.35em] text-[var(--admin-muted)]">
                            {task.relatedOrderId || 'Orden sin ID'}
                          </p>
                          <h3 className="text-xl font-semibold text-[var(--admin-text)]">
                            {task.title || 'Tarea sin título'}
                          </h3>
                          <p className="text-sm text-[var(--admin-muted)]">{task.description || 'Sin descripción.'}</p>
                        </div>
                        <div className="flex flex-col items-start gap-2 sm:items-end">
                          <span
                            className={[
                              'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide',
                              statusBadges[task.status] || 'bg-slate-100 text-slate-700',
                            ].join(' ')}
                          >
                            <StatusIcon className="h-4 w-4" />
                            {statusOptions.find((option) => option.value === task.status)?.label || task.status}
                          </span>
                          <select
                            className="rounded-full border border-[var(--admin-border)] bg-[var(--admin-surface)] px-3 py-1 text-xs font-semibold text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                            value={task.status}
                            disabled={updatingTask === task.id}
                            onChange={(event) => handleStatusUpdate(task.id, event.target.value)}
                          >
                            {statusOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-[var(--admin-muted)]">
                            {formatDate(task.dueDate)} · {formatRelative(task.dueDate)}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 grid gap-4 sm:grid-cols-3">
                        <div>
                          <p className="text-xs uppercase tracking-[0.35em] text-[var(--admin-muted)]">Cliente</p>
                          <p className="text-sm font-semibold text-[var(--admin-text)]">
                            {task.client?.name || 'Sin nombre'}{' '}
                            {task.client?.phone ? <span className="text-[var(--admin-muted)]">· {task.client.phone}</span> : null}
                          </p>
                          <p className="text-xs text-[var(--admin-muted)]">{task.client?.projectLocation || 'Ubicación pendiente'}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-[0.35em] text-[var(--admin-muted)]">Serie / cristal</p>
                          <p className="text-sm font-semibold text-[var(--admin-text)]">
                            {task.metadata?.lama || 'Lama pendiente'} · {task.metadata?.glass || 'Cristal por definir'}
                          </p>
                          <p className="text-xs text-[var(--admin-muted)]">{task.metadata?.color || 'Color por definir'}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-[0.35em] text-[var(--admin-muted)]">Montos</p>
                          <p className="text-sm font-semibold text-[var(--admin-text)]">
                            Depósito:{' '}
                            {task.metadata?.deposit
                              ? `$${Number(task.metadata.deposit).toFixed(2)}`
                              : 'Pendiente'}
                          </p>
                          <p className="text-xs text-[var(--admin-muted)]">
                            Total estimado:{' '}
                            {task.metadata?.total ? `$${Number(task.metadata.total).toFixed(2)}` : 'Pendiente'}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-[var(--admin-muted)]">
                        {(task.assignees || []).map((assignee) => (
                          <span
                            key={`${task.id}-${assignee.email}`}
                            className="inline-flex items-center gap-2 rounded-full border border-[var(--admin-border)] px-3 py-1 text-[var(--admin-text)]"
                          >
                            <UserGroupIcon className="h-4 w-4 text-[var(--admin-primary)]" />
                            {assignee.name || assignee.email}
                          </span>
                        ))}
                        {task.metadata?.notes && <span className="text-[var(--admin-text)]">· {task.metadata.notes}</span>}
                        <button
                          onClick={() => setExpandedId((prev) => (prev === task.id ? null : task.id))}
                          className="text-[var(--admin-primary)] underline-offset-2 hover:underline"
                        >
                          {expandedId === task.id ? 'Ocultar detalle' : 'Ver detalle completo'}
                        </button>
                      </div>

                      {expandedId === task.id && (
                        <div className="mt-4 grid gap-4 rounded-2xl border border-[var(--admin-border)] bg-[var(--admin-surface)] p-4 sm:grid-cols-2">
                          <div>
                            <p className="text-xs uppercase tracking-[0.35em] text-[var(--admin-muted)]">Modelos / medidas</p>
                            <ul className="mt-2 space-y-2 text-sm text-[var(--admin-text)]">
                              {(task.metadata?.items || []).map((item, index) => (
                                <li
                                  key={`${task.id}-item-${index}`}
                                  className="rounded-xl bg-white/70 px-3 py-2 text-[var(--admin-text)] shadow-sm"
                                >
                                  <p className="font-semibold">{item.name}</p>
                                  <p className="text-xs text-[var(--admin-muted)]">
                                    Cantidad: {item.quantity || '1'} {item.notes ? `· ${item.notes}` : ''}
                                  </p>
                                </li>
                              ))}
                              {!task.metadata?.items?.length && (
                                <li className="text-xs text-[var(--admin-muted)]">Sin modelos asociados.</li>
                              )}
                            </ul>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-[0.35em] text-[var(--admin-muted)]">Accesorios</p>
                            <ul className="mt-2 space-y-2 text-sm text-[var(--admin-text)]">
                              {(task.metadata?.accessories || []).map((addon, index) => (
                                <li key={`${task.id}-addon-${index}`} className="rounded-xl bg-white/70 px-3 py-2 shadow-sm">
                                  <p className="font-semibold">{addon.name}</p>
                                  <p className="text-xs text-[var(--admin-muted)]">
                                    {addon.quantity ? `${addon.quantity} uds` : 'Cantidad por confirmar'}{' '}
                                    {addon.notes ? `· ${addon.notes}` : ''}
                                  </p>
                                </li>
                              ))}
                              {!task.metadata?.accessories?.length && (
                                <li className="text-xs text-[var(--admin-muted)]">Sin accesorios añadidos.</li>
                              )}
                            </ul>
                          </div>
                          <div className="sm:col-span-2">
                            <p className="text-xs uppercase tracking-[0.35em] text-[var(--admin-muted)]">Últimos movimientos</p>
                            <ul className="mt-2 space-y-2 text-sm text-[var(--admin-text)]">
                              {(task.timeline || []).map((entry, index) => (
                                <li
                                  key={`${task.id}-timeline-${index}`}
                                  className="flex flex-col rounded-xl bg-white/70 px-3 py-2 shadow-sm sm:flex-row sm:items-center sm:justify-between"
                                >
                                  <div>
                                    <p className="font-semibold capitalize">{entry.action?.replace('_', ' ')}</p>
                                    {entry.notes && <p className="text-xs text-[var(--admin-muted)]">{entry.notes}</p>}
                                  </div>
                                  <div className="text-xs text-[var(--admin-muted)]">
                                    {entry.actor || 'Sistema'} · {formatDate(entry.createdAt)}
                                  </div>
                                </li>
                              ))}
                              {!task.timeline?.length && (
                                <li className="text-xs text-[var(--admin-muted)]">Sin movimientos registrados.</li>
                              )}
                            </ul>
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            </section>

            <aside className="space-y-6">
              <form onSubmit={handleCreateTask} className="rounded-3xl border border-[var(--admin-border)] bg-white p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">
                  Crear tarea manual
                </p>
                <h2 className="text-xl font-bold text-[var(--admin-text)]">Enviar instrucciones al equipo</h2>
                <p className="text-sm text-[var(--admin-muted)]">
                  Úsalo para órdenes que entraron fuera del flujo automatizado o para tareas internas (garantías, ajustes, etc.).
                </p>

                <div className="mt-4 space-y-4">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">
                      Tipo de tarea
                    </label>
                    <select
                      name="type"
                      value={form.type}
                      onChange={handleFormChange}
                      className="w-full rounded-2xl border border-[var(--admin-border)] bg-[var(--admin-surface)] px-3 py-2 text-sm font-medium text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                    >
                      {typeTabs
                        .filter((tab) => tab.value !== 'all')
                        .map((tab) => (
                          <option key={tab.value} value={tab.value}>
                            {tab.label}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">Título</label>
                    <input
                      name="title"
                      value={form.title}
                      onChange={handleFormChange}
                      className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-3 py-2 text-sm text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                      placeholder="Ej. Preparar serie Impact 5400"
                      required
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">Descripción</label>
                    <textarea
                      name="description"
                      value={form.description}
                      onChange={handleFormChange}
                      rows={3}
                      className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-3 py-2 text-sm text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                      placeholder="Notas rápidas para el equipo..."
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">
                      Cliente registrado
                    </label>
                    <select
                      value={selectedClientId}
                      onChange={(e) => handleClientSelection(e.target.value)}
                      className="w-full rounded-2xl border border-[var(--admin-border)] bg-[var(--admin-surface)] px-3 py-2 text-sm font-medium text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                    >
                      <option value="">
                        {clientsLoading ? 'Cargando clientes…' : 'Selecciona un cliente'}
                      </option>
                      {clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.businessName || client.name || client.email} · {client.email}
                        </option>
                      ))}
                    </select>
                    {selectedClientId && (
                      <p className="text-xs text-[var(--admin-muted)]">
                        Datos del cliente se completan automáticamente con la selección.
                      </p>
                    )}
                  </div>

                  {selectedClientId && (
                    <div className="space-y-1">
                      <label className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">
                        Factura asociada
                      </label>
                      <select
                        value={selectedInvoiceId}
                        onChange={(e) => handleInvoiceSelection(e.target.value)}
                        className="w-full rounded-2xl border border-[var(--admin-border)] bg-[var(--admin-surface)] px-3 py-2 text-sm font-medium text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                        disabled={invoicesLoading}
                      >
                        <option value="">
                          {invoicesLoading ? 'Cargando facturas…' : 'Selecciona una factura (opcional)'}
                        </option>
                        {clientInvoices.map((invoice) => (
                          <option key={invoice.id} value={invoice.id}>
                            #{invoice.id.slice(0, 6)} · $
                            {Number(invoice.total || 0).toFixed(2)} ·{' '}
                            {invoice.status || 'pendiente'}
                          </option>
                        ))}
                      </select>
                      {!clientInvoices.length && !invoicesLoading && (
                        <p className="text-xs text-[var(--admin-muted)]">
                          Este cliente aún no tiene facturas registradas.
                        </p>
                      )}
                    </div>
                  )}

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">
                        Orden / factura
                      </label>
                      <input
                        name="relatedOrderId"
                        value={form.relatedOrderId}
                        onChange={handleFormChange}
                        className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-3 py-2 text-sm text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                        placeholder="Ej. ORD-2043"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">
                        Lead / cliente
                      </label>
                      <input
                        name="relatedLeadId"
                        value={form.relatedLeadId}
                        onChange={handleFormChange}
                        className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-3 py-2 text-sm text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                        placeholder="ID del lead o email"
                      />
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">
                        ID de factura
                      </label>
                      <input
                        name="relatedInvoiceId"
                        value={form.relatedInvoiceId}
                        onChange={handleFormChange}
                        className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-3 py-2 text-sm text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                        placeholder="Ej. INV-2050"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)] flex items-center gap-2">
                        Factura adjunta (PDF / enlace)
                        <span className="text-[var(--admin-primary)] text-[10px] uppercase tracking-wide">Obligatorio</span>
                      </label>
                      <input
                        name="invoiceAttachmentUrl"
                        value={form.invoiceAttachmentUrl}
                        onChange={handleFormChange}
                        className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-3 py-2 text-sm text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                        placeholder="https://storage.googleapis.com/.../factura.pdf"
                        required
                      />
                      <p className="text-xs text-[var(--admin-muted)]">
                        Comparte el enlace seguro (Drive, Storage o PDF público). Si seleccionas una factura, rellenamos este campo por ti.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">Cliente</label>
                    <input
                      name="clientName"
                      value={form.clientName}
                      onChange={handleFormChange}
                      className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-3 py-2 text-sm text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                      placeholder="Nombre del cliente"
                      required
                    />
                    <div className="mt-2 grid gap-3 sm:grid-cols-2">
                      <input
                        name="clientEmail"
                        type="email"
                        value={form.clientEmail}
                        onChange={handleFormChange}
                        className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-3 py-2 text-sm text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                        placeholder="Email"
                      />
                      <input
                        name="clientPhone"
                        value={form.clientPhone}
                        onChange={handleFormChange}
                        className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-3 py-2 text-sm text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                        placeholder="Teléfono"
                      />
                    </div>
                    <input
                      name="projectLocation"
                      value={form.projectLocation}
                      onChange={handleFormChange}
                      className="mt-2 w-full rounded-2xl border border-[var(--admin-border)] bg-white px-3 py-2 text-sm text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                      placeholder="Municipio o urbanización"
                    />
                  </div>

                  <div className="grid gap-3 sm:grid-cols-3">
                    {[
                      { name: 'lama', label: 'Serie / Lama', placeholder: 'Lama 3' },
                      { name: 'glass', label: 'Cristal', placeholder: 'Impact + Low-E' },
                      { name: 'color', label: 'Color', placeholder: 'Blanco' },
                    ].map((field) => (
                      <div key={field.name} className="space-y-1">
                        <label className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">
                          {field.label}
                        </label>
                        <input
                          name={field.name}
                          value={form[field.name]}
                          onChange={handleFormChange}
                          className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-3 py-2 text-sm text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                          placeholder={field.placeholder}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">
                        Modelos (uno por línea · nombre | qty | notas)
                      </label>
                      <textarea
                        name="itemsText"
                        value={form.itemsText}
                        onChange={handleFormChange}
                        rows={3}
                        className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-3 py-2 text-sm text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                        placeholder="Casement XL | 4 | 72x48 · impacto
Awning Pro | 2 | cristal bronce"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">
                        Accesorios (nombre | qty | notas)
                      </label>
                      <textarea
                        name="accessoriesText"
                        value={form.accessoriesText}
                        onChange={handleFormChange}
                        rows={3}
                        className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-3 py-2 text-sm text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                        placeholder="Cerradura mariposa | 3
Silicone blanco | 6 tubos"
                      />
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">Depósito</label>
                      <input
                        name="deposit"
                        value={form.deposit}
                        onChange={handleFormChange}
                        className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-3 py-2 text-sm text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                        placeholder="Ej. 5000"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">Total</label>
                      <input
                        name="total"
                        value={form.total}
                        onChange={handleFormChange}
                        className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-3 py-2 text-sm text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                        placeholder="Ej. 15840"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">Fecha objetivo</label>
                    <input
                      type="date"
                      name="dueDate"
                      value={form.dueDate}
                      onChange={handleFormChange}
                      className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-3 py-2 text-sm text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">
                      Asignar a (opcional)
                    </label>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <input
                        name="assigneeName"
                        value={form.assigneeName}
                        onChange={handleFormChange}
                        className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-3 py-2 text-sm text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                        placeholder="Nombre"
                      />
                      <input
                        name="assigneeEmail"
                        type="email"
                        value={form.assigneeEmail}
                        onChange={handleFormChange}
                        className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-3 py-2 text-sm text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                        placeholder="Email"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">Notas internas</label>
                    <textarea
                      name="metadataNotes"
                      value={form.metadataNotes}
                      onChange={handleFormChange}
                      rows={3}
                      className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-3 py-2 text-sm text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                      placeholder="Indicaciones que deben ver los equipos asignados."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={creating}
                    className="w-full rounded-full bg-[var(--admin-primary)] px-4 py-3 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-[var(--admin-primary-dark)] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {creating ? 'Creando tarea...' : 'Crear tarea'}
                  </button>
                </div>
              </form>

              <div className="rounded-3xl border border-[var(--admin-border)] bg-[var(--admin-surface)] p-5 text-sm text-[var(--admin-muted)]">
                <h3 className="text-base font-semibold text-[var(--admin-text)]">Formato sugerido</h3>
                <ul className="mt-3 space-y-2">
                  <li>• Las líneas de modelos y accesorios aceptan separadores con “|” para indicar cantidad y notas.</li>
                  <li>
                    • Si asignas un correo, el sistema enviará notificación inmediata usando la lógica que ya teníamos para almacén /
                    técnicos.
                  </li>
                  <li>• Una vez almacén marca “completada”, se genera automáticamente la tarea de instalación / entrega.</li>
                  <li>• Adjunta el enlace del PDF de factura para que todos trabajen con el mismo documento.</li>
                </ul>
              </div>
            </aside>
          </div>
        </>
      )}

      {viewMode === 'team' && (
        <div className="grid gap-6 lg:grid-cols-[3fr,2fr]">
          <section className="rounded-3xl border border-[var(--admin-border)] bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-[var(--admin-muted)]">Equipo operativo</p>
                <h2 className="text-xl font-bold text-[var(--admin-text)]">Roles, accesos y permisos</h2>
              </div>
              <div className="inline-flex items-center gap-2 rounded-full border border-[var(--admin-border)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--admin-muted)]">
                <ShieldCheckIcon className="h-4 w-4 text-[var(--admin-primary)]" />
                {teamStats.total} activos
              </div>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {roleOptions.map((role) => (
                <div key={role.value} className="rounded-2xl border border-[var(--admin-border)] bg-[var(--admin-surface)] p-4">
                  <p className="text-xs uppercase tracking-[0.35em] text-[var(--admin-muted)]">{role.label}</p>
                  <p className="text-2xl font-semibold text-[var(--admin-text)]">{teamStats.byRole[role.value] || 0}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 overflow-x-auto">
              <table className="min-w-full divide-y divide-[var(--admin-border)] text-sm">
                <thead className="bg-[var(--admin-surface)] text-xs font-semibold uppercase tracking-[0.3em] text-[var(--admin-muted)]">
                  <tr>
                    <th className="px-4 py-2 text-left">Colaborador</th>
                    <th className="px-4 py-2 text-left">Rol</th>
                    <th className="px-4 py-2 text-left">Permisos</th>
                    <th className="px-4 py-2 text-left">Última actualización</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--admin-border)]">
                  {teamLoading && (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-center text-[var(--admin-muted)]">
                        Cargando equipo...
                      </td>
                    </tr>
                  )}
                  {!teamLoading && teamMembers.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-center text-[var(--admin-muted)]">
                        Aún no hay colaboradores registrados.
                      </td>
                    </tr>
                  )}
                  {teamMembers.map((member) => (
                    <tr key={member.id} className="hover:bg-[var(--admin-surface)]">
                      <td className="px-4 py-3">
                        <p className="font-semibold text-[var(--admin-text)]">{member.name || 'Sin nombre'}</p>
                        <p className="text-xs text-[var(--admin-muted)]">{member.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-2 rounded-full border border-[var(--admin-border)] px-3 py-1 text-xs font-semibold text-[var(--admin-text)]">
                          <UserGroupIcon className="h-4 w-4 text-[var(--admin-primary)]" />
                          {roleLabels[member.role] || member.role}
                        </span>
                        <p className="text-xs text-[var(--admin-muted)]">{member.department}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs text-[var(--admin-muted)]">
                          {(member.permissions || []).slice(0, 3).join(' • ') || 'Permisos por definir'}
                          {member.permissions?.length > 3 ? '…' : ''}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-xs text-[var(--admin-muted)]">
                        {formatDate(member.updatedAt || member.invitedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <aside className="space-y-6">
            <form onSubmit={handleInviteMember} className="rounded-3xl border border-[var(--admin-border)] bg-white p-5 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">Agregar colaborador</p>
              <h2 className="text-xl font-bold text-[var(--admin-text)]">Invita con un solo clic</h2>
              <p className="text-sm text-[var(--admin-muted)]">
                El sistema envía un enlace para que cree su contraseña y active el panel correspondiente.
              </p>

              <div className="mt-4 space-y-4">
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">Nombre</label>
                  <input
                    name="name"
                    value={teamForm.name}
                    onChange={handleTeamFormChange}
                    className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-3 py-2 text-sm text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                    placeholder="Nombre completo"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">Correo</label>
                  <input
                    type="email"
                    name="email"
                    value={teamForm.email}
                    onChange={handleTeamFormChange}
                    className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-3 py-2 text-sm text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                    placeholder="email@empresa.com"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">Teléfono</label>
                  <input
                    name="phone"
                    value={teamForm.phone}
                    onChange={handleTeamFormChange}
                    className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-3 py-2 text-sm text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                    placeholder="+1 787..."
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">Rol</label>
                  <select
                    name="role"
                    value={teamForm.role}
                    onChange={handleTeamFormChange}
                    className="w-full rounded-2xl border border-[var(--admin-border)] bg-[var(--admin-surface)] px-3 py-2 text-sm font-medium text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                  >
                    {roleOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-[var(--admin-muted)]">
                    {rolePermissionMap[teamForm.role]?.join(' • ') || 'Define permisos al guardar.'}
                  </p>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">Departamento</label>
                  <input
                    name="department"
                    value={teamForm.department}
                    onChange={handleTeamFormChange}
                    className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-3 py-2 text-sm text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                    placeholder="Ej. Ventas internas"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--admin-muted)]">Notas</label>
                  <textarea
                    name="notes"
                    value={teamForm.notes}
                    onChange={handleTeamFormChange}
                    rows={3}
                    className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-3 py-2 text-sm text-[var(--admin-text)] focus:border-[var(--admin-primary)] focus:outline-none"
                    placeholder="Observaciones internas, zonas, certificaciones requeridas..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={inviting}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--admin-primary)] px-4 py-3 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-[var(--admin-primary-dark)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <UserPlusIcon className="h-4 w-4" />
                  {inviting ? 'Enviando...' : 'Invitar colaborador'}
                </button>
              </div>
            </form>

            <div className="rounded-3xl border border-[var(--admin-border)] bg-[var(--admin-surface)] p-5 text-sm text-[var(--admin-muted)]">
              <h3 className="text-base font-semibold text-[var(--admin-text)]">Reglas de acceso</h3>
              <ul className="mt-3 space-y-2">
                <li>• Cada rol recibe vistas específicas dentro del panel (leads, almacén, técnicos).</li>
                <li>• Puedes editar permisos individuales en Firestore (`teamMembers`) si necesitas granularidad.</li>
                <li>• El correo enviado incluye enlace directo para configurar contraseña; no comparta claves genéricas.</li>
              </ul>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default OperationsBoard;
